import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  CLOSING_OUTCOMES,
  deriveMultiple,
  validateClosing,
  formatClosingMultiple,
  type ClosingOutcome,
} from "@shared/closingMath";
import { useClosingMutations, type Closing } from "@/hooks/useClosing";
import { useOffers } from "@/hooks/useBuyerTracker";
import { formatDealValue, parseDealValueToDollars } from "@/lib/formatDealValue";
import { useAuth } from "@/lib/auth";

const OTHER = "__other__";

/**
 * Records how a client engagement ended.
 *
 * Reached by setting a client's status to Closed, so that a Closed status can
 * never exist without a closing record. Money fields appear only for a sale,
 * mirroring the sold_has_terms CHECK constraint so the dialog cannot build a
 * payload the database will reject.
 */
export function CloseDealDialog({
  companyId,
  companyName,
  defaultEbitda,
  expectedFeePct,
  creditsDrawn,
  existing,
  open,
  onOpenChange,
}: {
  companyId: string;
  companyName: string;
  defaultEbitda: string | number | null;
  expectedFeePct?: string | number | null;
  /** Workfees + MVAs already drawn against this engagement's fee. */
  creditsDrawn?: number;
  existing?: Closing | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { save } = useClosingMutations(companyId);
  const { data: offers } = useOffers(companyId, open);
  const { canViewFinancials } = useAuth();

  const [outcome, setOutcome] = useState<ClosingOutcome>("Sold");
  const [closedOn, setClosedOn] = useState("");
  const [acquirerKey, setAcquirerKey] = useState<string>(OTHER);
  const [acquirerText, setAcquirerText] = useState("");
  const [finalEv, setFinalEv] = useState("");
  const [ebitda, setEbitda] = useState("");
  const [feeCollected, setFeeCollected] = useState("");
  const [notes, setNotes] = useState("");

  // Buyers who reached LOI are the plausible acquirers; IOI-only buyers are
  // included too since a deal can close off a verbal bump.
  const candidates = useMemo(() => {
    const seen = new Map<string, { buyerId: string; buyerName: string; loiEvMid: number | null }>();
    for (const row of [...(offers?.loi ?? []), ...(offers?.ioi ?? [])]) {
      if (seen.has(row.buyerId)) continue;
      const lo = Number(row.terms?.evLowEntered ?? 0);
      const hi = Number(row.terms?.evHighEntered ?? 0);
      seen.set(row.buyerId, {
        buyerId: row.buyerId,
        buyerName: row.buyerName,
        loiEvMid: lo > 0 && hi > 0 ? (lo + hi) / 2 : null,
      });
    }
    return [...seen.values()];
  }, [offers]);

  const selected = candidates.find((c) => c.buyerId === acquirerKey) ?? null;

  // Reset from the existing record (edit) or sane defaults (create) each open.
  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    setOutcome((existing?.outcome as ClosingOutcome) ?? "Sold");
    setClosedOn(existing?.closed_on ?? today);
    setAcquirerKey(existing?.acquirer_company_id ?? OTHER);
    setAcquirerText(existing?.acquirer_company_id ? "" : existing?.acquirer_name ?? "");
    setFinalEv(existing?.final_ev ? formatDealValue(existing.final_ev) : "");
    setEbitda(
      existing?.ebitda_at_close
        ? formatDealValue(existing.ebitda_at_close)
        : defaultEbitda
          ? formatDealValue(String(defaultEbitda))
          : ""
    );
    setFeeCollected(existing?.fee_collected ? formatDealValue(existing.fee_collected) : "");
    setNotes(existing?.notes ?? "");
  }, [open, existing, defaultEbitda]);

  // parseDealValueToDollars returns a string (it feeds decimal columns, where
  // going through a float would lose precision). Convert to number only for
  // the multiple math and the validation payload.
  const toNum = (s: string): number | null => {
    if (!s.trim()) return null;
    const parsed = parseDealValueToDollars(s);
    if (parsed == null) return null;
    const n = Number(parsed);
    return Number.isFinite(n) ? n : null;
  };

  const evNum = toNum(finalEv);
  const ebitdaNum = toNum(ebitda);
  const multiple = deriveMultiple(evNum, ebitdaNum);

  // Pre-fill: final EV x fee %, suggested not imposed. Only when the field is
  // untouched, only for granted users who can see the fee % at all.
  // Workfees and MVAs already drawn are advances against this fee, so the
  // suggestion is the balance still payable at close — not the gross fee, which
  // would double-count every draw already recorded as revenue.
  const grossFee = canViewFinancials && evNum != null && expectedFeePct != null
    ? Math.round(evNum * Number(expectedFeePct) / 100) : null;
  const suggestedFee = grossFee == null ? null : Math.max(0, grossFee - (creditsDrawn ?? 0));

  const payload = {
    outcome,
    closedOn: closedOn,
    acquirerCompanyId: selected ? selected.buyerId : null,
    acquirerName: selected ? null : acquirerText.trim() || null,
    finalEv: evNum,
    ebitdaAtClose: ebitdaNum,
    // Key omitted entirely without the grant, so the server preserves
    // whatever fee is already recorded rather than nulling it out.
    ...(canViewFinancials ? { feeCollected: toNum(feeCollected) } : {}),
  };
  const errors = validateClosing(payload);

  const submit = async () => {
    try {
      await save.mutateAsync({ ...payload, notes });
      toast.success(outcome === "Sold" ? "Deal closed" : `Engagement marked ${outcome}`);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(String(e?.message ?? e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Close deal, {companyName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Outcome</Label>
            <div className="flex gap-1.5 mt-1">
              {CLOSING_OUTCOMES.map((o) => (
                <Button
                  key={o}
                  type="button"
                  size="sm"
                  variant={outcome === o ? "default" : "outline"}
                  className="h-8 text-xs flex-1"
                  onClick={() => setOutcome(o)}
                >
                  {o}
                </Button>
              ))}
            </div>
            {outcome !== "Sold" && (
              <p className="text-xs text-muted-foreground mt-1.5">
                No terms recorded for an engagement that did not sell. It stays searchable and is
                excluded from track record numbers.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs">
              {outcome === "Sold" ? "Close date" : "Date the engagement ended"}
            </Label>
            <Input type="date" className="mt-1" value={closedOn} onChange={(e) => setClosedOn(e.target.value)} />
          </div>

          {outcome === "Sold" && (
            <>
              <div>
                <Label className="text-xs">Acquirer</Label>
                <select
                  className="mt-1 w-full h-9 rounded-md border bg-background px-3 text-sm"
                  value={acquirerKey}
                  onChange={(e) => {
                    setAcquirerKey(e.target.value);
                    // Pre-fill final EV from that buyer's offer so a re-trade is
                    // visible rather than silently overwritten.
                    const c = candidates.find((x) => x.buyerId === e.target.value);
                    if (c?.loiEvMid && !finalEv.trim()) setFinalEv(formatDealValue(String(c.loiEvMid)));
                  }}
                >
                  {candidates.map((c) => (
                    <option key={c.buyerId} value={c.buyerId}>
                      {c.buyerName}
                    </option>
                  ))}
                  <option value={OTHER}>Other / not in the CRM</option>
                </select>
                {acquirerKey === OTHER && (
                  <Input
                    className="mt-1.5"
                    placeholder="Acquirer name"
                    value={acquirerText}
                    onChange={(e) => setAcquirerText(e.target.value)}
                  />
                )}
                {selected?.loiEvMid != null && (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Their offer on file was {formatDealValue(String(selected.loiEvMid))}.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Final enterprise value</Label>
                  <Input
                    className="mt-1"
                    placeholder="e.g. $47M"
                    value={finalEv}
                    onChange={(e) => setFinalEv(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">EBITDA at close</Label>
                  <Input
                    className="mt-1"
                    placeholder="e.g. $9.4M"
                    value={ebitda}
                    onChange={(e) => setEbitda(e.target.value)}
                  />
                </div>
              </div>

              {canViewFinancials && (
                <div>
                  <Label className="text-xs">
                    Fee collected at close{creditsDrawn ? " (net of workfee drawn)" : ""}
                  </Label>
                  <Input
                    className="mt-1"
                    value={feeCollected}
                    onChange={(e) => setFeeCollected(e.target.value)}
                    placeholder={suggestedFee ? `Suggested: ${formatDealValue(String(suggestedFee))}` : "e.g. $1.2M"}
                  />
                  {suggestedFee != null && !feeCollected.trim() && (
                    <button
                      type="button"
                      className="text-xs text-primary-text text-left underline mt-1"
                      onClick={() => setFeeCollected(formatDealValue(String(suggestedFee)))}
                    >
                      Use suggested ({formatDealValue(String(suggestedFee))})
                    </button>
                  )}
                </div>
              )}

              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                Multiple <span className="font-semibold">{formatClosingMultiple(multiple)}</span>
                {". "}
                <span className="text-muted-foreground text-xs">
                  Derived from the two figures above, and frozen at these values.
                </span>
              </div>
            </>
          )}

          <div>
            <Label className="text-xs">Notes <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea className="mt-1 text-sm" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {errors.length > 0 && (
            <ul className="text-xs text-destructive space-y-0.5">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={errors.length > 0 || save.isPending}>
            {save.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
            ) : (
              <Check className="w-3.5 h-3.5 mr-1" />
            )}
            {existing ? "Save changes" : outcome === "Sold" ? "Record close" : `Mark ${outcome}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Read-only summary shown on a closed client. */
export function ClosedSummary({
  closing,
  onEdit,
  onReopen,
  reopening,
}: {
  closing: Closing | null;
  onEdit: () => void;
  onReopen: () => void;
  reopening?: boolean;
}) {
  const { canViewFinancials } = useAuth();
  // status='Closed' with no closing row is the pre-existing state of the two
  // deals closed before this feature shipped. It is a gap to fill, not an error.
  if (!closing) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap dark:border-amber-900 dark:bg-amber-950/40">
        <div className="text-sm">
          <span className="font-medium text-amber-900 dark:text-amber-200">Closed, outcome not recorded.</span>
          <span className="text-amber-800 ml-1.5 dark:text-amber-300">
            Add what happened so it counts toward the track record.
          </span>
        </div>
        <Button size="sm" onClick={onEdit}>Record outcome</Button>
      </div>
    );
  }

  const multiple = deriveMultiple(
    closing.final_ev == null ? null : Number(closing.final_ev),
    closing.ebitda_at_close == null ? null : Number(closing.ebitda_at_close)
  );
  const sold = closing.outcome === "Sold";

  return (
    <div className="rounded-lg border bg-muted/30 px-4 py-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap text-sm">
          <span
            className={cn(
              "text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border",
              sold
                ? "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                : "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-800"
            )}
          >
            {closing.outcome}
          </span>
          <span>
            <span className="text-muted-foreground">Closed </span>
            {closing.closed_on}
          </span>
          {sold && (
            <>
              <span>
                <span className="text-muted-foreground">Acquirer </span>
                {closing.acquirer_company_name || closing.acquirer_name || "n/a"}
              </span>
              <span>
                <span className="text-muted-foreground">EV </span>
                {closing.final_ev ? formatDealValue(closing.final_ev) : "n/a"}
              </span>
              <span>
                <span className="text-muted-foreground">Multiple </span>
                <span className="font-semibold">{formatClosingMultiple(multiple)}</span>
              </span>
              {canViewFinancials &&
                (closing.fee_collected ? (
                  <span>
                    <span className="text-muted-foreground">Fee </span>
                    <span className="font-semibold">{formatDealValue(closing.fee_collected)}</span>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="text-xs text-primary-text underline underline-offset-2"
                    onClick={onEdit}
                    title="No collected fee recorded for this sale"
                  >
                    Record fee
                  </button>
                ))}
            </>
          )}
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={onEdit}>Edit</Button>
          <Button size="sm" variant="outline" onClick={onReopen} disabled={reopening}>
            {reopening && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
            Reopen
          </Button>
        </div>
      </div>
      {closing.notes && <p className="text-xs text-muted-foreground mt-2">{closing.notes}</p>}
    </div>
  );
}
